import { computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Channel } from 'stream-chat';
import { finalize, take, tap } from 'rxjs';
import {
  ADS_TOKEN,
  AdsProvider,
  AdsUnitId,
  AppRoutes,
  MessagesRoutes,
  PLATFORM_TOKEN,
  PlatformProvider,
  stripDiacritics
} from '@core';
import { FeatureFlag, FeatureFlagHandler } from '@feature-flags';
import { BannerAdSize } from '@capacitor-community/admob/dist/esm/banner/banner-ad-size.enum';
import { BannerAdPosition } from '@capacitor-community/admob/dist/esm/banner/banner-ad-position.enum';
import { ChatInitializer } from '../../../application/initializers';
import { MessageContextStorage } from '../../../application/context-storage';
import { ChannelListItemViewService } from '../channel-list-item/view-service/channel-list-item.view-service';
import {
  STREAM_CHANNEL_TOKEN,
  STREAM_CHAT_CLIENT_TOKEN,
  StreamChannelProvider,
  StreamChatClientProvider
} from '@messages/infra';

export class ChatsListViewService {
  private readonly chatInitializer: ChatInitializer = inject(ChatInitializer);
  private readonly router: Router = inject(Router);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly adsProvider: AdsProvider = inject(ADS_TOKEN);
  private readonly featureFlagHandler: FeatureFlagHandler = inject(FeatureFlagHandler);
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);
  private readonly messageContextStorage: MessageContextStorage = inject(MessageContextStorage);
  private readonly channelListItemViewService: ChannelListItemViewService = inject(ChannelListItemViewService);

  private readonly isLoadingSignal: WritableSignal<boolean> = signal<boolean>(true);
  private readonly searchTermSignal: WritableSignal<string> = signal<string>('');

  readonly isLoading: Signal<boolean> = this.isLoadingSignal.asReadonly();
  readonly searchTerm: Signal<string> = this.searchTermSignal.asReadonly();
  readonly isChatAvailable: Signal<boolean | undefined> = toSignal(this.chatInitializer.isChatAvailable$);
  readonly channels: Signal<Channel[] | undefined> = toSignal(this.chatInitializer.channels$);
  readonly channelsVersion: Signal<number> = this.chatInitializer.channelsVersion;

  readonly filteredChannels: Signal<Channel[] | undefined> = computed(() => {
    const list: Channel[] | undefined = this.channels();
    const query: string = this.searchTermSignal().trim();
    if (!list || !query) return list;

    const normalizedQuery: string = stripDiacritics(query).toLowerCase();
    return list.filter((channel: Channel) => {
      const name: string = this.channelListItemViewService.getChannelName(channel);
      return stripDiacritics(name).toLowerCase().includes(normalizedQuery);
    });
  });

  readonly isiOS: boolean = this.platformProvider.isiOS;

  initChat(): void {
    this.isLoadingSignal.set(true);

    this.chatInitializer
      .init()
      .pipe(finalize(() => this.isLoadingSignal.set(false)))
      .subscribe();
  }

  setSearchTerm(value: string): void {
    this.searchTermSignal.set(value);
  }

  async selectChannel(channel: Channel): Promise<void> {
    this.captureUnreadState(channel);
    await this.channelProvider.setAsActiveChannel(channel);
    this.router.navigateByUrl(`${AppRoutes.MESSAGES}/${MessagesRoutes.CHAT}`);
  }

  initBottomBanner(): void {
    const bottomMargin: number = this.isiOS ? 50 : 70;

    this.featureFlagHandler
      .isEnabled$(FeatureFlag.AD_MOB)
      .pipe(
        take(1),
        tap((isEnabled: boolean) => {
          if (!isEnabled) return;

          this.adsProvider.showBanner({
            adSize: BannerAdSize.BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            adId: AdsUnitId.BannerV1,
            margin: bottomMargin
          });
        })
      )
      .subscribe();
  }

  private captureUnreadState(channel: Channel): void {
    const currentUserId: string | undefined = this.chatClientProvider.getCurrentUserId();
    if (!currentUserId) return;

    const readState = channel.state?.read?.[currentUserId];

    if (readState?.unread_messages && readState.last_read_message_id) {
      this.messageContextStorage.setUnreadSeparatorState(readState.last_read_message_id, readState.unread_messages);
      return;
    }

    this.messageContextStorage.clearUnreadSeparatorState();
  }
}
